import { useMemo, useState } from 'react';

import { useFilterWithExcludedCriteria } from './useFilterWithExcludedCriteria';

type HasEnumProp<T, E> = {
  [K in keyof T]: T[K] extends E ? K : never;
}[keyof T];

type EnumAggregateFn<T, E extends string | number> = (
  entities: T[]
) => Record<E, number>;

export const makeEnumAggregateFn = <
  T = any,
  E extends string | number = string | number,
  GP extends (entity: T) => E = (entity: T) => E,
  K extends HasEnumProp<T, E> = HasEnumProp<T, E>
>(
  enumObject: Record<string, E>,
  getProp: GP | K
): EnumAggregateFn<T, E> => {
  const initialValue = Object.values(enumObject).reduce(
    (acc, curr) => ({ ...acc, [curr]: 0 }),
    {} as Record<E, number>
  );
  return (entities) =>
    entities.reduce((acc, curr) => {
      const val = (
        typeof getProp === 'function' ? getProp(curr) : curr[getProp]
      ) as E;
      return {
        ...acc,
        [val]: acc[val] + 1,
      };
    }, initialValue);
};

export const useFiltersDropdownAggregate = <
  T = any,
  E extends string | number = string | number
>(
  criteria: string[] | string,
  aggregateFn: EnumAggregateFn<T, E>,
  enabled = true
) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const filteredData = useFilterWithExcludedCriteria(criteria, isDropdownOpen);
  const aggregate = useMemo(() => {
    return enabled
      ? aggregateFn(filteredData)
      : ({} as ReturnType<EnumAggregateFn<T, E>>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, enabled]);
  return {
    dropdownProps: enabled
      ? {
          onOpen: () => setIsDropdownOpen(true),
          onClose: () => setTimeout(() => setIsDropdownOpen(false)),
        }
      : {},
    aggregate,
  };
};
